import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CITY_BY_SLUG, CITY_SERVICE_BY_SLUG, CITY_SERVICES, CITIES } from "@/lib/cities";
import { SERVICE_BY_SLUG } from "@/lib/services";
import { CITY_SERVICE_CONTENT } from "@/lib/city-service-content";
import { isKeptCityService, KEPT_CITY_SERVICE_COPY } from "@/lib/kept-city-services";
import { getIndexedCombo, COMBO_SHORT_NAME } from "@/lib/indexed-combos";
import { INDEXED_COMBO_EXTRA } from "@/lib/indexed-combos-extra";
import { comboHref, uniqueByHref } from "@/lib/combo-links";
import { BUSINESS } from "@/lib/business";
import { PhoneButton } from "@/components/site/PhoneButton";
import { QuoteForm } from "@/components/site/QuoteForm";
import { TrustBar } from "@/components/site/TrustBar";
import { Testimonials } from "@/components/site/Testimonials";
import { FAQ } from "@/components/site/FAQ";
import { CTABand } from "@/components/site/CTABand";
import { faqSchema, breadcrumbSchema, serviceSchema } from "@/components/site/SchemaJsonLd";
import { Check, MapPin, Clock, ShieldCheck, Phone } from "lucide-react";
import cityHero from "@/assets/city-hero.webp";
import cityVan from "@/assets/city-van.webp";
import cityVan1024 from "@/assets/city-van-1024.webp";
import cityVan640 from "@/assets/city-van-640.webp";

export const Route = createFileRoute("/areas-served/$city/$service")({
  loader: ({ params }) => {
    const city = CITY_BY_SLUG[params.city];
    const cityService = CITY_SERVICE_BY_SLUG[params.service];
    if (!city || !cityService || !isKeptCityService(params.city, params.service)) throw notFound();
    const service = SERVICE_BY_SLUG[cityService.parentService];
    if (!service) throw notFound();
    return { city, cityService, service };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Not found" }] };
    const { city, cityService } = loaderData;
    const fullTitle = `${cityService.name} in ${city.name}, AZ | Phoenix Plumbing Co.`;
    // Keep titles ≤60 chars. Longest combos (e.g. "Water Heater Repair in
    // Paradise Valley, AZ | Phoenix Plumbing Co.") overflow — drop brand suffix.
    let title = fullTitle.length <= 60 ? fullTitle : `${cityService.name} in ${city.name}, AZ`;
    // Per-city hook keeps meta description unique across the 224 city×service combos.
    const localHook = city.localIssue.replace(/\s+/g, " ").trim();
    const rawDesc = `Licensed ${cityService.name.toLowerCase()} in ${city.name}, AZ — ${localHook} Same-day ${city.responseTime} response, flat-rate pricing. Call ${BUSINESS.phone}.`;
    const featured = getIndexedCombo(params.city, params.service);
    const shortName = COMBO_SHORT_NAME[cityService.slug] ?? cityService.name;
    if (featured) title = `${shortName} ${city.name} AZ | Phoenix Plumbing Co.`;
    const desc = featured
      ? featured.description
      : rawDesc.length <= 158 ? rawDesc : rawDesc.slice(0, 155) + "…";
    const SITE_URL = "https://www.phoenixplumbing.co";
    const path = `${SITE_URL}/areas-served/${params.city}/${params.service}`;
    const faqs = featured?.faqs ?? buildFaqs(city.name, cityService.name, city.responseTime);
    return {
      meta: [
        { title },
        ...(featured
          ? [{ name: "robots", content: "index, follow" }]
          : []),
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: path },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: path },
        { rel: "preload", as: "image", href: cityHero, fetchpriority: "high" } as any,
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(serviceSchema(`${featured ? shortName : cityService.name} in ${city.name}, AZ`, desc, city.name)),
        },
        { type: "application/ld+json", children: JSON.stringify(faqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", url: "/" },
              { name: "Areas Served", url: "/areas-served" },
              { name: city.name, url: `/areas-served/${params.city}` },
              { name: cityService.name, url: `/areas-served/${params.city}/${params.service}` },
            ]),
          ),
        },
      ],
    };
  },
  component: ServiceCityPage,
});

function buildFaqs(cityName: string, serviceName: string, responseTime: string) {
  return [
    {
      q: `How much does ${serviceName.toLowerCase()} cost in ${cityName}, AZ?`,
      a: `Most ${serviceName.toLowerCase()} jobs in ${cityName} are flat-rate quoted before any work begins. Common repairs run $150–$600, larger installations are quoted on-site. We never charge by the hour without telling you first.`,
    },
    {
      q: `Do you offer ${serviceName.toLowerCase()} in ${cityName}?`,
      a: `Yes. Phoenix Plumbing Co. dispatches ${serviceName.toLowerCase()} crews to ${cityName} during business hours — including holidays. Average ${cityName} response is ${responseTime}.`,
    },
    {
      q: `How quickly can a plumber get to my ${cityName} address?`,
      a: `${responseTime} is our typical ${cityName} arrival window. Active emergencies (no water, flooding, gas) are dispatched first.`,
    },
    {
      q: `Are your ${cityName} plumbers licensed and insured?`,
      a: `Every technician we send to ${cityName} is an AZ ROC licensed plumber, background-checked, drug-tested, and covered by our $2M general liability and full workers' comp.`,
    },
    {
      q: `Do you guarantee your ${serviceName.toLowerCase()} work in ${cityName}?`,
      a: `Yes — every ${serviceName.toLowerCase()} repair and installation in ${cityName} is backed by a written workmanship warranty. If something we did fails, we come back and fix it.`,
    },
    {
      q: `What ZIP codes do you cover in ${cityName}?`,
      a: `We cover every ZIP code in ${cityName} and the surrounding metro. Call ${BUSINESS.phone} with your address and we'll confirm dispatch time on the spot.`,
    },
  ];
}

// Per-service deep content blocks
const SERVICE_CONTENT: Record<
  string,
  {
    h1Lead: string;
    intro: (city: string) => string;
    bullets: (city: string) => string[];
    pricing: { item: string; price: string }[];
    signs: string[];
    process: { step: string; detail: (city: string) => string }[];
  }
> = {
  "emergency-plumber": {
    h1Lead: "Emergency Plumber",
    intro: (c) =>
      `When a pipe bursts, a sewer line backs up, or a water heater dumps 50 gallons across your floor, every minute counts. Phoenix Plumbing Co. answers ${c} emergency plumbing calls live, around the clock, and dispatches a licensed plumber with a fully stocked truck — usually arriving before the water shutoff drains your slab.`,
    bullets: (c) => [
      `Burst, frozen, or split pipe repair in ${c}`,
      `Sewer line backups and overflowing toilets`,
      `No-water and no-hot-water emergencies`,
      `Active gas leak detection and shutoff`,
      `Slab leak emergency containment`,
      `Sump pump and water-heater flood response`,
    ],
    pricing: [
      { item: "After-hours dispatch", price: "$89 flat" },
      { item: "Emergency repair (typical)", price: "$200 – $900" },
      { item: "Burst-pipe section replacement", price: "$350 – $1,200" },
      { item: "Sewer backup main-line clear", price: "$350 – $750" },
    ],
    signs: [
      "Water visibly running from a wall, ceiling, or fixture",
      "Smell of sewage in the house",
      "No water at any tap in the home",
      "Hissing sound near gas appliances or smell of rotten eggs",
      "Water heater leaking from the base",
    ],
    process: [
      { step: "Live phone triage", detail: (c) => `We answer. A real plumber asks the right questions and tells you exactly what to shut off in your ${c} home.` },
      { step: "Fast dispatch", detail: (c) => `Closest available ${c} truck is dispatched with the parts your job likely needs.` },
      { step: "Quote before work", detail: () => `Flat, written quote presented on-site. Nothing starts until you approve.` },
      { step: "Repair & cleanup", detail: () => `Repair is completed, area is cleaned, and warranty paperwork is left with you.` },
    ],
  },
  "water-heater-repair": {
    h1Lead: "Water Heater Repair & Installation",
    intro: (c) =>
      `Phoenix's hard water cuts the typical water heater lifespan to 8–12 years, and ${c} homes are no exception. Whether yours is leaking, cold, noisy, or just past its prime, we repair and install every major brand of tank, tankless, and hybrid heater — usually the same day you call.`,
    bullets: () => [
      "Leaking, rusted, or popping tanks",
      "No hot water / lukewarm water",
      "Pilot light, thermocouple, and igniter issues",
      "Tankless descaling and error-code repair",
      "Full tank-to-tankless conversions",
      "Expansion tanks, recirculation pumps, mixing valves",
    ],
    pricing: [
      { item: "Diagnostic", price: "$89 (credited toward repair)" },
      { item: "Element / thermostat repair", price: "$150 – $400" },
      { item: "40–50 gal tank replacement", price: "$1,500 – $2,800" },
      { item: "Tankless installation", price: "$3,200 – $5,500" },
    ],
    signs: [
      "Rusty or discolored hot water",
      "Popping or rumbling sounds from the tank",
      "Water pooling under or near the heater",
      "Hot water runs out in under 10 minutes",
      "Heater is 10+ years old",
    ],
    process: [
      { step: "On-site diagnostic", detail: (c) => `A licensed plumber tests the heater in your ${c} home and confirms repair vs replace.` },
      { step: "Flat quote", detail: () => `Repair or replacement options presented with written prices before any work.` },
      { step: "Same-day install", detail: () => `Most standard tank swaps are completed in 2–4 hours, same day.` },
      { step: "Warranty registration", detail: () => `We register manufacturer warranty in your name and back our labor in writing.` },
    ],
  },
  "drain-cleaning": {
    h1Lead: "Drain Cleaning & Clog Removal",
    intro: (c) =>
      `A clogged drain in ${c} is rarely just hair — hard-water scale traps grease, soap, and roots until the whole line backs up. We clear any drain in the house with the right tool, from a hand snake on a bathroom sink to high-pressure hydro-jetting on a main sewer line.`,
    bullets: () => [
      "Kitchen sink, bathroom sink, tub, and shower clogs",
      "Toilet clogs deep enough to need a closet auger",
      "Main line backups and tree-root intrusion",
      "Hydro-jetting for grease, scale, and biofilm",
      "Sewer camera inspection with on-screen recording",
      "Cleanout install for recurring backups",
    ],
    pricing: [
      { item: "Single drain clearing", price: "$129 – $249" },
      { item: "Main line clearing", price: "$250 – $550" },
      { item: "Hydro-jet main line", price: "$450 – $900" },
      { item: "Sewer camera inspection", price: "$250 (free with clearing)" },
    ],
    signs: [
      "Multiple drains slow at the same time",
      "Gurgling sounds from toilet or tub",
      "Sewage smell coming from drains",
      "Water backing up into the tub when the toilet flushes",
      "Wet spots in the yard near sewer cleanout",
    ],
    process: [
      { step: "Camera inspection", detail: (c) => `We scope the line in your ${c} home before clearing so we know exactly what's blocking it.` },
      { step: "Right tool for the line", detail: () => `Hand snake, drum machine, or hydro-jet — matched to the pipe material and clog type.` },
      { step: "Clear & verify", detail: () => `Drain is cleared and we run water to confirm full flow before leaving.` },
      { step: "Prevention plan", detail: () => `If the clog is recurring, we recommend cleanouts, jetting schedules, or pipe lining.` },
    ],
  },
  "leak-detection": {
    h1Lead: "Leak Detection & Slab Leak Repair",
    intro: (c) =>
      `A hidden leak in a ${c} slab home can waste 10,000+ gallons a month before you ever see a stain. We use thermal imaging, acoustic listening devices, and pressure testing to pinpoint the leak in hours — then repair it with the least possible disruption to your home.`,
    bullets: () => [
      "Slab leaks under concrete foundations",
      "Pinhole leaks in copper supply lines",
      "Behind-wall and ceiling leaks",
      "Yard / main supply line leaks",
      "Pool plumbing and irrigation leaks",
      "Whole-home pressure testing",
    ],
    pricing: [
      { item: "Leak location", price: "$250 – $450" },
      { item: "Pinhole copper repair", price: "$350 – $900" },
      { item: "Slab leak access & repair", price: "$1,200 – $3,500" },
      { item: "Slab leak re-route", price: "$2,500 – $5,500" },
    ],
    signs: [
      "Unexplained spike in your water bill",
      "Warm spots on the floor",
      "Sound of running water with everything off",
      "Mildew smell or damp baseboards",
      "Foundation cracks or settling",
    ],
    process: [
      { step: "Pressure test", detail: () => `We isolate hot, cold, and irrigation to confirm where the leak is.` },
      { step: "Locate", detail: (c) => `Thermal + acoustic tools pinpoint the leak in your ${c} home, often to within a foot.` },
      { step: "Repair options", detail: () => `Spot repair, re-route, or repipe — quoted with pros, cons, and price.` },
      { step: "Restore", detail: () => `We patch concrete or drywall to a paint-ready finish.` },
    ],
  },
  "sewer-line-repair": {
    h1Lead: "Sewer Line Repair & Replacement",
    intro: (c) =>
      `${c}'s clay soil and mature mesquite roots are hard on sewer lines. We camera every line before quoting, then choose trenchless pipe lining when possible to avoid tearing up your yard, driveway, or landscaping.`,
    bullets: () => [
      "Root intrusion clearing and removal",
      "Sewer camera inspections with recording",
      "Trenchless pipe lining (CIPP)",
      "Pipe bursting for full replacement",
      "Full open-trench sewer replacement",
      "Cleanout installation for future access",
    ],
    pricing: [
      { item: "Sewer camera inspection", price: "$250" },
      { item: "Hydro-jet root removal", price: "$450 – $900" },
      { item: "Trenchless pipe lining", price: "$90 – $250 / ft" },
      { item: "Full sewer replacement", price: "$5,500 – $18,000" },
    ],
    signs: [
      "Multiple drains backing up at once",
      "Sewage smell in the yard",
      "Lush green strip of grass over the sewer line",
      "Frequent main-line clogs (more than once a year)",
      "Sinkholes or wet spots in the yard",
    ],
    process: [
      { step: "Camera", detail: (c) => `We scope from cleanout to city tap in your ${c} home and record what we see.` },
      { step: "Diagnose", detail: () => `Roots, bellies, breaks, off-grade — we explain exactly what's wrong.` },
      { step: "Trenchless when possible", detail: () => `If the pipe is intact, CIPP lining cures in hours with no digging.` },
      { step: "Restore & warranty", detail: () => `Yard restored, work warrantied in writing, video left with you.` },
    ],
  },
  "plumbing-service": {
    h1Lead: "Residential Plumbing Service",
    intro: (c) =>
      `From 1950s ranch homes to brand-new builds, Phoenix Plumbing Co. handles every residential plumbing job in ${c}. Upfront flat-rate pricing, licensed technicians, and a written workmanship guarantee on every job — no exceptions.`,
    bullets: () => [
      "Faucets, toilets, sinks, showers, and tubs",
      "Water heaters (tank & tankless)",
      "Drain clogs and slow drains",
      "Slab leaks and pipe repairs",
      "Whole-home repipes (PEX or copper)",
      "Water softeners, filtration, and RO",
    ],
    pricing: [
      { item: "Service call / diagnostic", price: "$89" },
      { item: "Common repairs", price: "$150 – $500" },
      { item: "Fixture installation", price: "$175 – $450" },
      { item: "Full repipe", price: "$4,500 – $12,000" },
    ],
    signs: [
      "Drips, leaks, or low pressure",
      "Constantly running toilet",
      "Slow drains anywhere in the house",
      "Old galvanized or polybutylene pipe",
      "Hard-water scale on fixtures",
    ],
    process: [
      { step: "Book online or call", detail: (c) => `Most ${c} appointments scheduled same or next day.` },
      { step: "On-time arrival", detail: () => `We text 30 minutes out with the technician's name and photo.` },
      { step: "Quote before work", detail: () => `Flat written price. You approve before anything starts.` },
      { step: "Clean job site", detail: () => `Drop cloths in, vacuum out. You won't know we were there.` },
    ],
  },
  "gas-line": {
    h1Lead: "Gas Line Installation & Repair",
    intro: (c) =>
      `Gas line work in ${c} is not DIY territory — it requires a licensed plumber, a pulled permit, a pressure test, and a city inspection. We install, repair, extend, and re-route gas lines for water heaters, ranges, dryers, pool heaters, fire pits, and BBQs across ${c}, all to current Phoenix code.`,
    bullets: (c) => [
      `New gas line runs for ranges, dryers, and BBQs in ${c}`,
      "Gas leak detection and emergency shutoff",
      "Black-iron and CSST (flexible) gas piping",
      "Pool heater and fire-pit gas hookups",
      "Gas line extensions for kitchen remodels",
      "Pressure testing and city re-inspection",
    ],
    pricing: [
      { item: "Gas leak detection", price: "$150 – $350" },
      { item: "New gas line run (per linear ft)", price: "$25 – $45 / ft" },
      { item: "Appliance gas hookup", price: "$250 – $550" },
      { item: "Full gas line replacement", price: "$1,500 – $4,500" },
    ],
    signs: [
      "Smell of rotten eggs or sulfur indoors or near the meter",
      "Hissing sound near a gas appliance",
      "Dead grass or plants directly above a buried gas line",
      "Higher than normal gas bill with no usage change",
      "Pilot lights that won't stay lit",
    ],
    process: [
      { step: "Permit & plan", detail: (c) => `We pull the ${c} gas permit and map the run before any pipe is cut.` },
      { step: "Install or repair", detail: () => `Black iron or CSST sized to BTU load — never undersized to save time.` },
      { step: "Pressure test", detail: () => `Air-test to code spec and document the reading for the inspector.` },
      { step: "Final inspection", detail: () => `We meet the city inspector and don't leave until the line is signed off.` },
    ],
  },
  "toilet-repair": {
    h1Lead: "Toilet Repair & Installation",
    intro: (c) =>
      `A running, leaking, or clogged toilet wastes hundreds of gallons a day and shows up fast on a ${c} water bill. We rebuild, repair, and install one-piece, two-piece, comfort-height, and wall-hung toilets across ${c} — same day on most calls, with the wax ring, supply line, and flange hardware on the truck.`,
    bullets: () => [
      "Constantly running or phantom-flushing toilets",
      "Clogged toilets a plunger can't clear",
      "Cracked tanks, bowls, or broken flanges",
      "Wax ring leaks and floor stains around the base",
      "Full toilet replacements (1.28 GPF / dual-flush)",
      "Comfort-height and ADA-compliant installs",
    ],
    pricing: [
      { item: "Toilet rebuild (flapper, fill valve, supply)", price: "$150 – $275" },
      { item: "Wax ring & flange repair", price: "$200 – $450" },
      { item: "Toilet replacement (standard)", price: "$350 – $650" },
      { item: "High-end / wall-hung install", price: "$700 – $1,800" },
    ],
    signs: [
      "Water on the floor around the toilet base",
      "Tank refills on its own every few minutes",
      "Bowl drains slowly even when not clogged",
      "Rocking or loose toilet when you sit down",
      "Cracks visible in the tank or bowl",
    ],
    process: [
      { step: "Diagnose the failure", detail: (c) => `Most ${c} toilet issues are flapper, fill valve, or flange — we pinpoint in minutes.` },
      { step: "Repair or replace quote", detail: () => `Honest call on whether a $200 rebuild beats a $500 swap for your fixture.` },
      { step: "Swap with no mess", detail: () => `Old toilet hauled away, new wax ring and stainless bolts, floor cleaned.` },
      { step: "Leak test", detail: () => `Multiple flush cycles and a dye test before we call it done.` },
    ],
  },
  "faucet-installation": {
    h1Lead: "Faucet & Fixture Installation",
    intro: (c) =>
      `From a dripping kitchen faucet to a full bathroom fixture upgrade, we install every major brand in ${c} — Moen, Delta, Kohler, Brizo, Hansgrohe, Pfister, Grohe. ${c}'s hard water destroys cartridges and O-rings, so we also stock brand-specific repair kits for in-warranty rebuilds.`,
    bullets: () => [
      "Kitchen and bar faucet installation",
      "Bathroom lavatory, tub, and shower faucets",
      "Hands-free / touchless faucet installs",
      "Pot fillers and pre-rinse kitchen sprayers",
      "Cartridge, O-ring, and stem rebuilds",
      "Hose bibs and outdoor spigots",
    ],
    pricing: [
      { item: "Faucet cartridge rebuild", price: "$125 – $275" },
      { item: "Kitchen faucet installation", price: "$175 – $350" },
      { item: "Bath fixture set (faucet + drain)", price: "$250 – $500" },
      { item: "Pot filler installation", price: "$450 – $900" },
    ],
    signs: [
      "Constant drip even when fully closed",
      "Reduced flow at one fixture (clogged aerator or cartridge)",
      "Handle is stiff or won't return to off",
      "Green or white crust around the base",
      "Hot/cold reversed after a recent repair",
    ],
    process: [
      { step: "Confirm the right part", detail: (c) => `Brand and model lookup before we drive — no second trips on ${c} jobs.` },
      { step: "Shut off, swap, seal", detail: () => `Angle stops tested, supply lines replaced, plumber's putty or silicone where it belongs.` },
      { step: "Flush the lines", detail: () => `Cartridge protected from debris during initial water-on.` },
      { step: "Walk-through", detail: () => `Hot/cold confirmed, leak-checked, handle action shown to you.` },
    ],
  },
  "repiping": {
    h1Lead: "Repiping & Pipe Repair",
    intro: (c) =>
      `${c} homes built in the late 1970s through early 1990s often have polybutylene supply lines that fail at the fittings without warning, and pre-1970 homes commonly have galvanized branch lines that have rusted to half their original inner diameter. We do spot pipe repairs and full whole-home repipes (PEX-A or Type L copper) across ${c}, with drywall patching included.`,
    bullets: () => [
      "Polybutylene whole-home repipe (PEX-A or copper)",
      "Galvanized supply line replacement",
      "Pinhole copper leak section repair",
      "Re-routes through attic or walls (slab avoidance)",
      "Manifold and home-run PEX installs",
      "Drywall patching and paint-ready finish",
    ],
    pricing: [
      { item: "Single pipe section repair", price: "$250 – $650" },
      { item: "Slab leak re-route", price: "$2,500 – $5,500" },
      { item: "Partial repipe (bath or kitchen)", price: "$1,800 – $4,500" },
      { item: "Full whole-home PEX repipe", price: "$4,500 – $12,000" },
    ],
    signs: [
      "Multiple leaks in a short timespan",
      "Discolored or rusty water from cold taps",
      "Low pressure throughout the entire house",
      "Gray plastic (poly) pipe visible at the water heater",
      "Old galvanized pipe visible at exposed connections",
    ],
    process: [
      { step: "Whole-home walk", detail: (c) => `We map every fixture, drop, and manifold location in your ${c} home.` },
      { step: "Material decision", detail: () => `PEX-A for flex and pinhole-proof runs, Type L copper where code or HOA requires it.` },
      { step: "Phased water-off", detail: () => `Most homes stay water-on overnight; we only kill main supply during active connections.` },
      { step: "Patch & paint-ready", detail: () => `Drywall patched, textured, primed — ready for your painter.` },
    ],
  },
  "water-softener": {
    h1Lead: "Water Softener & Filtration",
    intro: (c) =>
      `${c} water runs 12–20+ grains per gallon — among the hardest in the country — and that mineral content is the #1 reason ${c} water heaters die at 8 years, fixtures crust over in 18 months, and skin and hair feel dry year-round. We size, install, and service whole-home softeners, RO drinking systems, and salt-free conditioners for every ${c} ZIP.`,
    bullets: () => [
      "Whole-home water softener installation",
      "Reverse osmosis (RO) drinking water systems",
      "Salt-free water conditioners",
      "Carbon and sediment pre-filtration",
      "Existing softener resin replacement",
      "Softener bypass and loop installation",
    ],
    pricing: [
      { item: "Softener service & resin replacement", price: "$350 – $800" },
      { item: "Whole-home softener install", price: "$1,800 – $3,500" },
      { item: "Under-sink RO system install", price: "$650 – $1,400" },
      { item: "Softener + RO combo package", price: "$2,400 – $4,800" },
    ],
    signs: [
      "White scale on faucets, glass, and shower doors",
      "Soap that won't lather and skin that feels filmy",
      "Water heater popping or rumbling (sediment)",
      "Existing softener no longer regenerates",
      "Chlorine taste or smell at drinking taps",
    ],
    process: [
      { step: "Test the water", detail: (c) => `We measure ${c} hardness and TDS on-site before sizing any equipment.` },
      { step: "Size for the household", detail: () => `Grain capacity matched to people + fixtures — no undersized builder-grade units.` },
      { step: "Install at the loop", detail: (c) => `Most ${c} homes have a pre-installed loop; we tie in cleanly, no garage rework.` },
      { step: "Program & verify", detail: () => `Regeneration scheduled, bypass shown to you, post-install hardness tested at 0 gpg.` },
    ],
  },
  "garbage-disposal": {
    h1Lead: "Garbage Disposal Repair & Installation",
    intro: (c) =>
      `A jammed, leaking, or humming garbage disposal is one of the most common kitchen calls we run in ${c}. We repair, reset, and replace every major brand — Insinkerator, Waste King, Moen, Kohler — and most jobs are completed in under an hour with a new unit on the truck.`,
    bullets: () => [
      "Disposal won't turn on (humming or silent)",
      "Leaks from the bottom housing or top flange",
      "Jammed flywheel and reset-button service",
      "Full disposal replacement (1/2 HP to 1 HP)",
      "Air switches and batch-feed conversions",
      "Dishwasher drain line connection",
    ],
    pricing: [
      { item: "Disposal unjam & reset", price: "$125 – $200" },
      { item: "Replace standard 1/2 HP disposal", price: "$275 – $450" },
      { item: "Replace 3/4 – 1 HP disposal", price: "$425 – $700" },
      { item: "Add air switch with disposal", price: "+$150 – $275" },
    ],
    signs: [
      "Disposal hums but doesn't spin",
      "Water leaking under the sink when disposal runs",
      "Dishwasher backs up into the sink",
      "Sharp grinding or rattling noise",
      "Persistent smell that cleaning won't fix",
    ],
    process: [
      { step: "Diagnose in minutes", detail: (c) => `Most ${c} disposal calls are jam, reset, or seal — we know within five minutes.` },
      { step: "Repair or replace", detail: () => `Honest call: a $150 reset beats a $400 swap when the motor is good.` },
      { step: "Quiet, clean swap", detail: () => `New mounting ring, new plumber's putty, dishwasher line reconnected.` },
      { step: "Run test", detail: () => `Full water and disposal cycle with cold water before we close up.` },
    ],
  },
  "backflow-prevention": {
    h1Lead: "Backflow Prevention & Testing",
    intro: (c) =>
      `${c} requires annual backflow testing on every irrigation, fire-suppression, and commercial water service to keep contaminated water from siphoning back into the city supply. We are certified ${c} backflow testers, file the report with the water utility on your behalf, and repair or replace any device that fails.`,
    bullets: () => [
      "Annual residential and commercial backflow testing",
      "RPZ (Reduced Pressure Zone) device testing",
      "Double-check valve assembly testing",
      "Pressure vacuum breaker (PVB) testing",
      "Backflow repair, rebuild, and replacement",
      "Utility report filing on your behalf",
    ],
    pricing: [
      { item: "Residential backflow test (per device)", price: "$75 – $125" },
      { item: "Commercial backflow test (per device)", price: "$125 – $200" },
      { item: "RPZ rebuild kit installation", price: "$275 – $550" },
      { item: "Backflow device replacement", price: "$650 – $1,800" },
    ],
    signs: [
      "Annual test notice from your water utility",
      "Continuous drip from the RPZ relief valve",
      "Drop in irrigation pressure after rain",
      "Discolored water at hose bibs after irrigation runs",
      "City water shutoff notice for missed testing",
    ],
    process: [
      { step: "Schedule the test", detail: (c) => `Most ${c} residential tests are 20 minutes and don't shut off house water.` },
      { step: "Test & document", detail: () => `Calibrated gauge readings recorded on the city's official test form.` },
      { step: "Repair on the spot", detail: () => `Rebuild kits on the truck for the most common RPZ and DCV failures.` },
      { step: "File with utility", detail: () => `We submit your pass directly to the water provider — you don't lift a finger.` },
    ],
  },
  "hydro-jetting": {
    h1Lead: "Hydro Jetting",
    intro: (c) =>
      `Snaking punches a hole through a clog — hydro jetting scours the entire pipe wall back to bare metal. For ${c} homes with grease-loaded kitchen lines, scale-narrowed mains, or aggressive tree-root intrusion, hydro jetting is the only honest fix. We camera every line before jetting so you see what's actually in there.`,
    bullets: () => [
      "Main sewer line hydro jetting",
      "Kitchen line grease removal",
      "Tree root cutting and removal",
      "Hard-water scale and biofilm removal",
      "Pre- and post-jet camera inspection",
      "Commercial restaurant grease-line jetting",
    ],
    pricing: [
      { item: "Sewer camera inspection", price: "$250 (credited)" },
      { item: "Residential main hydro-jet", price: "$450 – $900" },
      { item: "Kitchen line hydro-jet", price: "$350 – $650" },
      { item: "Commercial grease line jetting", price: "$650 – $1,800" },
    ],
    signs: [
      "Recurring main-line backups (more than once a year)",
      "Multiple drains slow after the line was snaked",
      "Gurgling toilets even after clearing",
      "Camera footage showing scale or root regrowth",
      "Commercial kitchen with frequent grease backups",
    ],
    process: [
      { step: "Camera first", detail: (c) => `We scope your ${c} line so jet pressure is matched to pipe material and condition.` },
      { step: "Jet to bare pipe", detail: () => `4,000 PSI rotating nozzle cuts grease, roots, and scale — not just punches through.` },
      { step: "Re-camera proof", detail: () => `Post-jet video shows a clean line wall, not just clear flow.` },
      { step: "Prevention plan", detail: () => `Annual jet schedule for grease-heavy or root-prone lines so this doesn't repeat.` },
    ],
  },
};

function ServiceCityPage() {
  const { city, cityService } = Route.useLoaderData();
  const content = SERVICE_CONTENT[cityService.slug];
  const faqs = buildFaqs(city.name, cityService.name, city.responseTime);
  const otherCities = uniqueByHref(CITIES.filter((c) => c.slug !== city.slug).slice(0, 8), (c) => comboHref(c.slug, cityService.slug));
  const otherServices = uniqueByHref(CITY_SERVICES.filter((s) => s.slug !== cityService.slug), (s) => comboHref(city.slug, s.slug));
  const uniqueParas = CITY_SERVICE_CONTENT[city.slug]?.[cityService.slug] ?? [];
  const keptCopy = KEPT_CITY_SERVICE_COPY[`${city.slug}/${cityService.slug}`];
  const featured = getIndexedCombo(city.slug, cityService.slug);
  const parent = Route.useLoaderData().service;
  const pageFaqs = featured?.faqs ?? faqs;
  const extra = featured ? INDEXED_COMBO_EXTRA[`${city.slug}/${cityService.slug}`] : undefined;

  return (
    <>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="border-b border-border bg-card">
        <ol className="mx-auto max-w-7xl px-4 py-3 flex flex-wrap gap-2 text-sm text-muted-foreground">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li>/</li>
          <li><Link to="/areas-served" className="hover:text-primary">Areas Served</Link></li>
          <li>/</li>
          <li>
            <Link to="/areas-served/$city" params={{ city: city.slug }} className="hover:text-primary">
              {city.name}
            </Link>
          </li>
          <li>/</li>
          <li className="text-secondary font-semibold">{cityService.name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-secondary to-secondary/90 text-secondary-foreground overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${cityHero})` }} />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/85 to-secondary/45" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 md:py-16 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="text-accent font-bold text-sm uppercase tracking-widest flex items-center gap-2">
              <MapPin className="h-4 w-4" /> {cityService.name} • {city.name}, AZ
            </p>
            <h1 className="mt-2 text-3xl md:text-5xl font-extrabold tracking-tight">
              {featured ? (COMBO_SHORT_NAME[cityService.slug] ?? content.h1Lead) : content.h1Lead} in {city.name}, AZ
            </h1>
            <p className="mt-3 text-lg text-secondary-foreground/90">
              The {city.name} homeowner's first call for fast, fair, licensed plumbing.
            </p>
            <p className="mt-5 text-secondary-foreground/85 leading-relaxed">
              {featured ? featured.heroIntro : keptCopy?.heroIntro ?? content.intro(city.name)}
            </p>
            {featured && (
              <p className="mt-3 text-secondary-foreground/85 leading-relaxed">
                A licensed Phoenix Plumbing Co. technician is on the way to your {city.name} home in{" "}
                <strong className="text-accent">{city.responseTime}</strong>.
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <PhoneButton size="lg" />
              <a href="#quote" className="inline-flex items-center justify-center rounded-md border-2 border-accent px-6 py-4 text-base font-extrabold text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                Free On-Site Quote
              </a>
            </div>
            <div className="mt-6"><TrustBar dark /></div>
          </div>
          <div id="quote" className="lg:mt-2">
            <QuoteForm defaultService={`${cityService.name} in ${city.name}`} />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-6 grid gap-4 sm:grid-cols-3">
          <Stat icon={<Clock className="h-5 w-5" />} label={`${city.name} Response`} value={city.responseTime} />
          <Stat icon={<ShieldCheck className="h-5 w-5" />} label="Licensed" value={BUSINESS.license} />
          <Stat icon={<Phone className="h-5 w-5" />} label="Dispatch" value={BUSINESS.phone} href={BUSINESS.phoneHref} />
        </div>
      </section>

      {/* What we fix */}
      <section className="mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-[1.3fr_1fr] items-start">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
            What our {city.name} {cityService.name.toLowerCase()} crew handles
          </h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {content.bullets(city.name).map((b) => (
              <li key={b} className="flex gap-2 text-foreground">
                <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <h3 className="mt-10 text-xl font-bold text-secondary">
            Signs you need {cityService.name.toLowerCase()} in {city.name} now
          </h3>
          <ul className="mt-3 space-y-2">
            {(extra?.signs ?? content.signs).map((s) => (
              <li key={s} className="flex gap-2 text-foreground">
                <span className="text-accent font-bold">▸</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-xl border-l-4 border-accent bg-muted/40 p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              What {city.name} homes see most
            </div>
            <p className="mt-2 text-foreground font-medium">{city.localIssue}</p>
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
          <img srcSet={`${cityVan640} 640w, ${cityVan1024} 1024w, ${cityVan} 1264w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1264} height={848} decoding="async" src={cityVan} alt={`Phoenix Plumbing Co. ${cityService.name.toLowerCase()} truck serving ${city.name}, AZ`} className="w-full h-auto block" loading="lazy" />
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-5xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
            {cityService.name} pricing in {city.name}, AZ
          </h2>
          {featured ? (
            <>
              <p className="mt-3 text-foreground">{parent.pricingNote}</p>
              <div className="mt-6 rounded-xl border border-border bg-card px-5 py-4 font-bold text-secondary">{parent.priceRange}</div>
              {parent.pricingBullets && (
                <ul className="mt-4 space-y-2">
                  {parent.pricingBullets.map((b) => (
                    <li key={b} className="flex gap-2 text-foreground"><Check className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span>{b}</span></li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-muted-foreground">{BUSINESS.license}. Final quote provided on-site after diagnosis.</p>
            </>
          ) : (<>
          <p className="mt-3 text-foreground">
            Flat, upfront pricing — quoted before any work begins. {city.name} customers never pay an unknown hourly rate.
          </p>
          <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-left">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th className="px-5 py-3 font-bold">Service</th>
                  <th className="px-5 py-3 font-bold">{city.name} Price Range</th>
                </tr>
              </thead>
              <tbody>
                {content.pricing.map((p) => (
                  <tr key={p.item} className="border-t border-border">
                    <td className="px-5 py-3 font-medium text-foreground">{p.item}</td>
                    <td className="px-5 py-3 text-secondary font-bold">{p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Pricing ranges reflect typical {city.name} residential jobs. Final quote provided on-site after diagnosis.
          </p>
          </>)}
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
          How {cityService.name.toLowerCase()} works in {city.name}
        </h2>
        <ol className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {content.process.map((p, i) => (
            <li key={p.step} className="rounded-xl border border-border bg-card p-5">
              <div className="text-xs font-bold text-accent-ink tracking-widest uppercase">Step {i + 1}</div>
              <div className="mt-1 font-extrabold text-secondary text-lg">{p.step}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.detail(city.name)}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Neighborhoods */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold">
            {cityService.name} across {city.name} neighborhoods
          </h2>
          <p className="mt-3 text-secondary-foreground/85 max-w-3xl">
            We dispatch {cityService.name.toLowerCase()} crews to every corner of {city.name} daily — including:
          </p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {city.neighborhoods.map((n: string) => (
              <li key={n} className="rounded-full bg-secondary-foreground/10 text-secondary-foreground px-3 py-1 text-sm font-semibold border border-secondary-foreground/15">
                {n}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Local Context — deep on-page SEO for the city × service combo */}
      <section className="mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-[1.4fr_1fr] items-start">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
            Why {city.name}, AZ homes need {cityService.name.toLowerCase()} more than most
          </h2>
          {featured ? (
            <div className="mt-4 space-y-4">
              {featured.paras.map((p, i) => (
                <p key={i} className="text-foreground leading-relaxed">{p}</p>
              ))}
              {extra && <p className="text-foreground leading-relaxed">{extra.closing}</p>}
              {extra?.closing2 && <p className="text-foreground leading-relaxed">{extra.closing2}</p>}
            </div>
          ) : (
            <p className="mt-4 text-foreground leading-relaxed">{keptCopy?.why ?? city.localContext}</p>
          )}
          {!featured && uniqueParas.length > 0 && (
            <div className="mt-6 space-y-4">
              {uniqueParas.map((p, i) => (
                <p key={i} className="text-foreground leading-relaxed">{p}</p>
              ))}
            </div>
          )}
          <p className="mt-4 text-foreground leading-relaxed">
            <strong className="text-secondary">Local water:</strong> {city.waterUtility}
          </p>
          <p className="mt-4 text-foreground leading-relaxed">
            Whether your home is near {city.landmarks.slice(0, 3).join(", ")}, or anywhere else in {city.name}, our
            licensed {cityService.name.toLowerCase()} crews dispatch in {city.responseTime} with a fully stocked truck.
            We arrive in marked {BUSINESS.name} vehicles, in uniform, with floor protection — every job, every {city.name} ZIP.
          </p>

          <h3 className="mt-8 text-xl font-bold text-secondary">
            {city.name} ZIP codes we cover for {cityService.name.toLowerCase()}
          </h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {city.zips.map((z: string) => (
              <li key={z} className="rounded-md bg-muted text-secondary px-2.5 py-1 text-sm font-mono font-semibold">
                {z}
              </li>
            ))}
          </ul>

          <h3 className="mt-8 text-xl font-bold text-secondary">
            Landmarks near our {city.name} {cityService.name.toLowerCase()} jobs
          </h3>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {city.landmarks.map((l: string) => (
              <li key={l} className="flex gap-2 text-foreground">
                <MapPin className="h-4 w-4 text-primary mt-1 shrink-0" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
        <aside className="rounded-2xl border border-border bg-card p-6 shadow-sm sticky top-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
            Common Plumbing Services in {city.name}
          </div>
          <p className="mt-3 text-sm text-foreground">
            Phoenix Plumbing Co. helps homeowners and businesses in {city.name} with water
            heater repair, drain cleaning, leak detection, sewer line repair, toilet repair,
            faucet installation, garbage disposal repair, gas line plumbing, and emergency
            plumbing service.
          </p>
          <div className="mt-5">
            <PhoneButton className="w-full justify-center" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground text-center">
            {city.name} dispatch • {city.responseTime} typical arrival
          </p>
        </aside>
      </section>

      {/* Reviews */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
          {city.name} reviews of our {cityService.name.toLowerCase()} crews
        </h2>
        <div className="mt-6"><Testimonials count={3} city={city.name} /></div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary mb-6">
            {cityService.name} FAQ — {city.name}, AZ
          </h2>
          <FAQ items={pageFaqs} />
        </div>
      </section>

      {featured && (
        <section className="mx-auto max-w-7xl px-4 pt-12">
          <h2 className="text-xl font-extrabold text-secondary mb-4">More for {city.name} homeowners</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Link to="/areas-served/$city" params={{ city: city.slug }} className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors">
              <div className="font-bold text-secondary">Plumber in {city.name}, AZ</div>
            </Link>
            <Link to={`/${parent.slug}` as any} className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors">
              <div className="font-bold text-secondary">{parent.name} across the Valley</div>
            </Link>
            {uniqueByHref(featured.siblings, (k) => { const [cs, ss] = k.split("/"); return comboHref(cs, ss); }).filter((k) => { const [cs, ss] = k.split("/"); return comboHref(cs, ss) !== `/${parent.slug}`; }).map((k) => {
              const [cs, ss] = k.split("/");
              const sib = CITY_SERVICE_BY_SLUG[ss];
              return (
                <Link key={k} to={comboHref(cs, ss) as any} className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors">
                  <div className="font-bold text-secondary">{COMBO_SHORT_NAME[ss] ?? sib?.name} in {city.name}, AZ</div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Related city services */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-xl font-extrabold text-secondary mb-4">
          Other plumbing services in {city.name}, AZ
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {otherServices.map((s) => (
            <Link
              key={s.slug}
              to={comboHref(city.slug, s.slug) as any}
              className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors"
            >
              <div className="font-bold text-secondary">{s.name} in {city.name}</div>
              <div className="text-xs text-muted-foreground mt-1">Same-day • Licensed</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Same service in other cities */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-xl font-extrabold text-secondary mb-4">
          {cityService.name} in other Valley cities
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {otherCities.map((c) => (
            <Link
              key={c.slug}
              to={comboHref(c.slug, cityService.slug) as any}
              className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors"
            >
              <div className="font-bold text-secondary">{cityService.name} {c.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.responseTime} response</div>
            </Link>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}

function Stat({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string; href?: string }) {
  const inner = (
    <div className="flex items-center gap-3">
      <div className="rounded-full bg-primary/10 text-primary p-2">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
        <div className="font-extrabold text-secondary">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : <div>{inner}</div>;
}